import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { APP_PATH } from "../../api/backendPath";
import { toast } from "../../components/toast";

const getAuthHeader = () => ({
  Authorization: `Bearer ${sessionStorage.getItem("usr_1a2b3c")}`,
});

/**
 * Technician genders this imaging center may register, as assigned by the admin
 * ("Gender Preference" on the imaging user → imaging_centers.allows_male_staff /
 * allows_female_staff, returned by GET /imaging/profile).
 *
 * A missing flag counts as permitted, matching the backend column default, so centers
 * created before the setting existed stay unrestricted. If the profile can't be loaded
 * both genders are offered; the backend still rejects a gender that isn't permitted.
 * @returns {Promise<string[]>} Subset of ["Male", "Female"], never empty
 */
export const getPermittedTechnicianGenders = async () => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}${APP_PATH.imagingProfile}`,
      { headers: getAuthHeader() },
    );
    const center = response.data?.data || {};
    const genders = [];
    if (center.allows_male_staff !== false) genders.push("Male");
    if (center.allows_female_staff !== false) genders.push("Female");
    return genders.length ? genders : ["Male", "Female"];
  } catch (error) {
    console.error("Error fetching permitted technician genders:", error);
    return ["Male", "Female"];
  }
};

// Fetch technicians list
export const fetchImagingTechnicians = createAsyncThunk(
  "imagingTechnicians/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const { page = 1, limit = 10, search, status } = params;
      const q = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (search && search.trim()) q.append("search", search.trim());
      if (status && status !== "All" && status !== "ALL")
        q.append("status", status.toUpperCase());

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}${APP_PATH.imagingTechnicians}?${q.toString()}`,
        { headers: getAuthHeader() },
      );
      const d = response.data?.data;
      return {
        technicians: d?.technicians || [],
        pagination: d?.pagination || {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 1,
        },
      };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch technicians",
      );
    }
  },
);

// Add new technician
export const addImagingTechnician = createAsyncThunk(
  "imagingTechnicians/add",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}${APP_PATH.imagingTechnicians}`,
        data,
        { headers: { ...getAuthHeader(), "Content-Type": "application/json" } },
      );
      if (response.data?.status) {
        toast.success("Technician added successfully");
        return response.data?.data;
      }
      return rejectWithValue(
        response.data?.message || "Failed to add technician",
      );
    } catch (error) {
      const msg = error?.response?.data?.message || "Failed to add technician";
      toast.error(msg);
      return rejectWithValue(msg);
    }
  },
);

// Get technician details
export const fetchTechnicianDetails = createAsyncThunk(
  "imagingTechnicians/fetchDetails",
  async (technicianId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}${APP_PATH.imagingTechnicians}/${technicianId}`,
        { headers: getAuthHeader() },
      );
      return response.data?.data || null;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch technician details",
      );
    }
  },
);

// Get technician assignments
export const fetchTechnicianAssignments = createAsyncThunk(
  "imagingTechnicians/fetchAssignments",
  async (
    { technicianId, dateFrom, dateTo, status, page = 1, limit = 10 },
    { rejectWithValue },
  ) => {
    try {
      const q = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (dateFrom) q.append("date_from", dateFrom);
      if (dateTo) q.append("date_to", dateTo);
      if (status && status !== "ALL") q.append("status", status);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}${APP_PATH.imagingTechnicians}/${technicianId}/assignments?${q.toString()}`,
        { headers: getAuthHeader() },
      );
      return (
        response.data?.data || {
          assignments: [],
          technician: null,
          pagination: {},
        }
      );
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch assignments",
      );
    }
  },
);

// Update technician
export const updateImagingTechnician = createAsyncThunk(
  "imagingTechnicians/update",
  async ({ technicianId, data }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}${APP_PATH.imagingTechnicians}/${technicianId}`,
        data,
        { headers: { ...getAuthHeader(), "Content-Type": "application/json" } },
      );
      if (response.data?.status) {
        toast.success("Technician updated successfully");
        return response.data?.data;
      }
      return rejectWithValue(
        response.data?.message || "Failed to update technician",
      );
    } catch (error) {
      const msg =
        error?.response?.data?.message || "Failed to update technician";
      toast.error(msg);
      return rejectWithValue(msg);
    }
  },
);

// Delete (deactivate) technician
export const deleteImagingTechnician = createAsyncThunk(
  "imagingTechnicians/delete",
  async (technicianId, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}${APP_PATH.imagingTechnicians}/${technicianId}`,
        { headers: getAuthHeader() },
      );
      if (response.data?.status) {
        toast.success("Technician deleted successfully");
        return response.data?.data;
      }
      // The gateway reports failures (e.g. active assignments) as
      // `status: false` in the body, not as an HTTP error.
      const failMsg = response.data?.message || "Failed to delete technician";
      toast.error(failMsg);
      return rejectWithValue(failMsg);
    } catch (error) {
      const msg =
        error?.response?.data?.message || "Failed to delete technician";
      toast.error(msg);
      return rejectWithValue(msg);
    }
  },
);

const imagingTechniciansSlice = createSlice({
  name: "imagingTechnicians",
  initialState: {
    technicians: [],
    pagination: { page: 1, limit: 10, total: 0, totalPages: 1 },
    selectedTechnician: null,
    assignments: [],
    assignmentsPagination: {},
    isLoading: false,
    isDetailsLoading: false,
    isAssignmentsLoading: false,
    isSaving: false,
    error: null,
  },
  reducers: {
    clearSelectedTechnician: (state) => {
      state.selectedTechnician = null;
      state.assignments = [];
      state.assignmentsPagination = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchImagingTechnicians.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchImagingTechnicians.fulfilled, (state, action) => {
        state.isLoading = false;
        state.technicians = action.payload.technicians;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchImagingTechnicians.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Add
      .addCase(addImagingTechnician.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(addImagingTechnician.fulfilled, (state) => {
        state.isSaving = false;
      })
      .addCase(addImagingTechnician.rejected, (state) => {
        state.isSaving = false;
      })
      // Details
      .addCase(fetchTechnicianDetails.pending, (state) => {
        state.isDetailsLoading = true;
      })
      .addCase(fetchTechnicianDetails.fulfilled, (state, action) => {
        state.isDetailsLoading = false;
        state.selectedTechnician = action.payload;
      })
      .addCase(fetchTechnicianDetails.rejected, (state) => {
        state.isDetailsLoading = false;
      })
      // Assignments
      .addCase(fetchTechnicianAssignments.pending, (state) => {
        state.isAssignmentsLoading = true;
      })
      .addCase(fetchTechnicianAssignments.fulfilled, (state, action) => {
        state.isAssignmentsLoading = false;
        state.assignments = action.payload.assignments || [];
        state.assignmentsPagination = action.payload.pagination || {};
        if (action.payload.technician)
          state.selectedTechnician = action.payload.technician;
      })
      .addCase(fetchTechnicianAssignments.rejected, (state) => {
        state.isAssignmentsLoading = false;
      })
      // Update
      .addCase(updateImagingTechnician.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(updateImagingTechnician.fulfilled, (state) => {
        state.isSaving = false;
      })
      .addCase(updateImagingTechnician.rejected, (state) => {
        state.isSaving = false;
      })
      // Delete
      .addCase(deleteImagingTechnician.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(deleteImagingTechnician.fulfilled, (state) => {
        state.isSaving = false;
      })
      .addCase(deleteImagingTechnician.rejected, (state) => {
        state.isSaving = false;
      });
  },
});

export const { clearSelectedTechnician } = imagingTechniciansSlice.actions;
export default imagingTechniciansSlice.reducer;
